import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import * as XLSX from 'xlsx';

const ReportLayer = () => {
    const [assets, setAssets] = useState([]);
    const [filteredAssets, setFilteredAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filter states
    const [filters, setFilters] = useState({
        category: '',
        condition: '',
        dateFrom: '',
        dateTo: '',
        location: '',
        department: '',
        holderUser: '',
        serialNo: ''
    });

    // Statistics
    const [statistics, setStatistics] = useState({
        totalAssets: 0,
        totalValue: 0,
        totalDepreciation: 0,
        byCategory: [],
        byCondition: []
    });

    useEffect(() => {
        fetchAssets();
        fetchStatistics();
    }, []);

    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, assets]);

    const fetchAssets = async () => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/assets?per_page=1000`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch assets: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setAssets(data.data);
                setFilteredAssets(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch assets');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/assets/statistics`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setStatistics(data.data);
            }
        } catch (err) {
            console.error('Failed to fetch statistics:', err);
        }
    };

    const applyFilters = () => {
        let filtered = [...assets];

        // Category filter
        if (filters.category) {
            filtered = filtered.filter(asset => asset.asset_category === filters.category);
        }

        // Condition filter
        if (filters.condition) {
            filtered = filtered.filter(asset => asset.condition === filters.condition);
        }

        // Date range filter
        if (filters.dateFrom) {
            filtered = filtered.filter(asset => 
                new Date(asset.date_acquired) >= new Date(filters.dateFrom)
            );
        }

        if (filters.dateTo) {
            filtered = filtered.filter(asset => 
                new Date(asset.date_acquired) <= new Date(filters.dateTo)
            );
        }

        // Location filter
        if (filters.location) {
            filtered = filtered.filter(asset => 
                asset.location?.toLowerCase().includes(filters.location.toLowerCase())
            );
        }

        // Department filter
        if (filters.department) {
            filtered = filtered.filter(asset => 
                asset.department?.toLowerCase().includes(filters.department.toLowerCase())
            );
        }

        // Holder/User filter
        if (filters.holderUser) {
            filtered = filtered.filter(asset => 
                asset.holder_user?.toLowerCase().includes(filters.holderUser.toLowerCase())
            );
        }

        // Serial Number filter
        if (filters.serialNo) {
            filtered = filtered.filter(asset => 
                asset.serial_number?.toLowerCase().includes(filters.serialNo.toLowerCase())
            );
        }

        setFilteredAssets(filtered);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            category: '',
            condition: '',
            dateFrom: '',
            dateTo: '',
            location: '',
            department: '',
            holderUser: '',
            serialNo: ''
        });
    };

    const handleExportCSV = () => {
        // CSV headers
        const headers = [
            'S.No', 'Item Name', 'Tagging Number', 'Category', 'Serial Number',
            'Location', 'Department', 'Holder/User', 'Condition', 'Date Acquired',
            'Original Cost', 'Net Book Value', 'Accumulated Depreciation',
            'Current Month Depreciation', 'Yearly Depreciation', 'Useful Life'
        ];

        // CSV rows
        const rows = filteredAssets.map((asset, index) => [
            index + 1,
            asset.item_name || '',
            asset.tagging_number || '',
            asset.asset_category || '',
            asset.serial_number || '',
            asset.location || '',
            asset.department || '',
            asset.holder_user || '',
            asset.condition || '',
            asset.date_acquired || '',
            asset.original_cost || '0',
            asset.net_book_value || '0',
            asset.accumulated_depreciation || '0',
            asset.current_month_depreciation || '0',
            asset.yearly_depreciation || '0',
            asset.useful_life || '0'
        ]);

        // Create CSV content
        let csvContent = headers.join(',') + '\n';
        rows.forEach(row => {
            csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
        });

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `asset_report_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportExcel = () => {
        // Prepare data for Excel
        const excelData = filteredAssets.map((asset, index) => ({
            'S.No': index + 1,
            'Item Name': asset.item_name || '',
            'Tagging Number': asset.tagging_number || '',
            'Category': asset.asset_category || '',
            'Serial Number': asset.serial_number || '',
            'Location': asset.location || '',
            'Department': asset.department || '',
            'Holder/User': asset.holder_user || '',
            'Condition': asset.condition || '',
            'Date Acquired': asset.date_acquired ? new Date(asset.date_acquired).toISOString().split('T')[0] : '',
            'Original Cost': asset.original_cost || '0',
            'Net Book Value': asset.net_book_value || '0',
            'Accumulated Depreciation': asset.accumulated_depreciation || '0',
            'Current Month Depreciation': asset.current_month_depreciation || '0',
            'Yearly Depreciation': asset.yearly_depreciation || '0',
            'Useful Life': asset.useful_life ? `${asset.useful_life} years` : '0'
        }));

        // Create workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Asset Report');

        // Set column widths
        const columnWidths = [
            { wch: 6 },   // S.No
            { wch: 25 },  // Item Name
            { wch: 18 },  // Tagging Number
            { wch: 20 },  // Category
            { wch: 18 },  // Serial Number
            { wch: 15 },  // Location
            { wch: 15 },  // Department
            { wch: 15 },  // Holder/User
            { wch: 12 },  // Condition
            { wch: 15 },  // Date Acquired
            { wch: 15 },  // Original Cost
            { wch: 15 },  // Net Book Value
            { wch: 20 },  // Accumulated Depreciation
            { wch: 22 },  // Current Month Depreciation
            { wch: 18 },  // Yearly Depreciation
            { wch: 12 }   // Useful Life
        ];
        worksheet['!cols'] = columnWidths;

        // Generate Excel file and download
        const fileName = `asset_report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const handlePrint = () => {
        window.print();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getConditionBadge = (condition) => {
        const badges = {
            excellent: 'bg-success-focus text-success-600 border border-success-main',
            good: 'bg-info-focus text-info-600 border border-info-main',
            fair: 'bg-warning-focus text-warning-600 border border-warning-main',
            poor: 'bg-danger-focus text-danger-600 border border-danger-main',
            damaged: 'bg-neutral-200 text-neutral-600 border border-neutral-400'
        };
        return badges[condition] || badges.good;
    };

    // Calculate filtered statistics
    const filteredStats = {
        totalAssets: filteredAssets.length,
        totalValue: filteredAssets.reduce((sum, asset) => sum + parseFloat(asset.original_cost || 0), 0),
        totalDepreciation: filteredAssets.reduce((sum, asset) => sum + parseFloat(asset.accumulated_depreciation || 0), 0),
        netBookValue: filteredAssets.reduce((sum, asset) => sum + parseFloat(asset.net_book_value || 0), 0)
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-lg fw-semibold mb-0">Asset Report</h6>
            </div>

            <div className="card-body p-24">
                {/* Filters Section */}
                <div className="card border mb-24">
                    <div className="card-body p-20">
                        <h6 className="text-md fw-semibold mb-16">Filters</h6>
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">Category</label>
                                <select
                                    className="form-select"
                                    name="category"
                                    value={filters.category}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Categories</option>
                                    <option value="Vehicles">Vehicles</option>
                                    <option value="Equipments">Equipments</option>
                                    <option value="Furniture and fixture">Furniture and fixture</option>
                                </select>
                                </div>
                            <div className="col-md-3">
                                <label className="form-label">Condition</label>
                                <select
                                    className="form-select"
                                    name="condition"
                                    value={filters.condition}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Conditions</option>
                                    <option value="excellent">Excellent</option>
                                    <option value="good">Good</option>
                                    <option value="fair">Fair</option>
                                    <option value="poor">Poor</option>
                                    <option value="damaged">Damaged</option>
                                </select>
                                </div>
                            <div className="col-md-3">
                                <label className="form-label">Date From</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                />
                                </div>
                            <div className="col-md-3">
                                <label className="form-label">Date To</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                />
                                </div>
                            <div className="col-md-3">
                                <label className="form-label">Location</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="location"
                                    placeholder="Search location..."
                                    value={filters.location}
                                    onChange={handleFilterChange}
                                />
                                </div>
                            <div className="col-md-3">
                                <label className="form-label">Department</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="department"
                                    placeholder="Search department..."
                                    value={filters.department}
                                    onChange={handleFilterChange}
                                />
                                </div>
                            <div className="col-md-3">
                                <label className="form-label">Holder/User</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="holderUser"
                                    placeholder="Search holder/user..."
                                    value={filters.holderUser}
                                    onChange={handleFilterChange}
                                />
                                </div>
                            <div className="col-md-3">
                                <label className="form-label">Serial No.</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="serialNo"
                                    placeholder="Search serial number..."
                                    value={filters.serialNo}
                                    onChange={handleFilterChange}
                                />
                                </div>
                            <div className="col-12">
                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    className="btn btn-outline-danger-600 radius-8"
                                >
                                    <Icon icon="ic:round-clear" className="text-xl me-1" />
                                    Reset Filters
                                </button>
                            </div>
                                </div>
                                </div>
                                </div>

                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                                </div>
                )}

                {/* Export Excel Button */}
                <div className="d-flex justify-content-end mb-24">
                    <button
                        type="button"
                        onClick={handleExportExcel}
                        className="btn btn-success-600 radius-8 px-20 py-11"
                        disabled={filteredAssets.length === 0}
                    >
                        <Icon icon="vscode-icons:file-type-excel" className="text-xl me-2" />
                        Export Excel
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                                </div>
                                </div>
                ) : (
                    <>
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">S.L</th>
                                        <th scope="col">Item Name</th>
                                        <th scope="col">Tagging No.</th>
                                        <th scope="col">Category</th>
                                        <th scope="col">Serial No.</th>
                                        <th scope="col">Location</th>
                                        <th scope="col">Department</th>
                                        <th scope="col">Holder/User</th>
                                        <th scope="col">Condition</th>
                                        <th scope="col">Date Acquired</th>
                                        <th scope="col">Original Cost</th>
                                        <th scope="col">Net Book Value</th>
                                        <th scope="col">Depreciation</th>
                                        <th scope="col">Useful Life</th>
                        </tr>
                                </thead>
                                <tbody>
                                    {filteredAssets.length === 0 ? (
                                        <tr>
                                            <td colSpan="14" className="text-center py-4">
                                                No assets found matching the filter criteria
                            </td>
                        </tr>
                                    ) : (
                                        filteredAssets.map((asset, index) => (
                                            <tr key={asset.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.item_name}
                                </span>
                            </td>
                            <td>
                                                    <span className="badge bg-primary-50 text-primary-600 px-12 py-4 radius-4 fw-medium text-sm">
                                                        {asset.tagging_number}
                                </span>
                            </td>
                            <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.asset_category}
                                </span>
                            </td>
                            <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.serial_number || 'N/A'}
                                </span>
                            </td>
                            <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.location || 'N/A'}
                                </span>
                            </td>
                            <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.department || 'N/A'}
                                </span>
                            </td>
                            <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.holder_user || 'N/A'}
                                </span>
                            </td>
                                                <td className="text-center">
                                                    <span
                                                        className={`${getConditionBadge(
                                                            asset.condition
                                                        )} px-24 py-4 radius-4 fw-medium text-sm text-capitalize`}
                                                    >
                                                        {asset.condition}
                                </span>
                            </td>
                                                <td>{formatDate(asset.date_acquired)}</td>
                                                <td>
                                                    <span className="fw-semibold">
                                                        {formatCurrency(asset.original_cost)}
                                </span>
                            </td>
                            <td>
                                                    <span className="fw-semibold text-success-600">
                                                        {formatCurrency(asset.net_book_value)}
                                </span>
                            </td>
                            <td>
                                                    <span className="fw-semibold text-danger-600">
                                                        {formatCurrency(asset.accumulated_depreciation)}
                                </span>
                            </td>
                            <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.useful_life ? `${asset.useful_life} years` : 'N/A'}
                                </span>
                            </td>
                        </tr>
                                        ))
                                    )}
                    </tbody>
                </table>
            </div>
                    </>
                )}
        </div>

            {/* Print Styles */}
            <style jsx>{`
                @media print {
                    .btn, .filter-section, .card-header button {
                        display: none !important;
                    }
                    .table {
                        font-size: 10px;
                    }
                    .card {
                        border: none;
                        box-shadow: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default ReportLayer;
